/**
 * NGX Application Flow
 * States, Actions, Action Methods
 *
 * NGX.appFlow.init();
 *
 * returns an Object with the States and Actions
 */

import { initOverlayHandler } from './DisplayItem';

import { resizeCanvas, capitalize, asyncFn, isRef, trim } from '../utils/utils';
import { openWindow } from '../utils/browser';
import { updateClassByPrefix, addInlineScript, createCloseButtons } from '../utils/domUtils';
import { removeFormListeners } from '../utils/formUtils';

import DisplayItem from './DisplayItem';
import { AllAnalyticsEvents } from '../../common/config/api.js';

/**

 Flow specific events. these are handled within appflow to allow beforeX + blocking.

 NGX.App.api.register(String event, Function, [Bool blocking]);

 If optional param blocking is true, the callback is passed as a argument
 to the function rather than executed immediately. This only applies to
 beforeX events since the others are triggered after anyway.

 'beforeState:entry'
 'beforeAction:entry'

 'onState:entry'
 'onAction:entry'

 State events. These are broadcast globally.

 NGX.App.Events.on(String event, Function)

 'state'        => String, state name
 'state:name'   => Object, state

 'tab:show'     => Array, components on visible section
 'tab:hide'     => Array, components on section being hidden

 'action'       => String, caller action
 'action:name'  => Undefined, returns the output of the called function

 */
export default (function(root, d, $, json) {
  'use strict';

  var body = d.body,
    _state = false,
    _action = false,
    _prev = false,
    _current = false,
    stateInit = {},
    actionInit = {},
    actionJSON = json.actions,
    initialPage = true;

  /** State Object cache */

  // protect the app flow from tampering TODO: add methods to update this object via the api?
  var stateCache = (function(raw) {
    var out = {},
      item,
      i = 0,
      len;
    if (raw) {
      len = raw.length;
      for (; i < len; i++) {
        item = raw[i];
        if (item.name) {
          out[item.name] = item;
        }
      }
    }
    return out;
  })(json.states);

  // Is this a start route (else it's a route that is derived from the start route)?
  var isStartRoute = 'start' in json.routes;

  /** Utility functions */

  /**
   * Return action ref as Object
   * @param {string} refStr
   * @returns {{method: string, action: string, target: string, params}}
   */

  var getRefObj = function(refStr) {
      var refArr = refStr.split(':');
      return {
        method: refArr[0],
        action: refArr[1],
        target: refArr[2],
        // pass any additional params as a string array
        // TODO: New sharing moved away from using a params array to .apply() to functions, should consider replacing this in exec and game methods to ensure consistency.
        params: (function(a) {
          var out = [],
            len = a.length,
            i = 2;
          if (len >= i) {
            for (; i < len; i++) {
              if (a[i].length > 0) {
                out.push(a[i] + '');
              }
            }
          }
          return out;
        })(refArr),
      };
    },
    /**
     * Screen position is not called for overlays
     */
    screenPosition = function() {
      // resize the FB frame. this is a bit of a hack.
      if (NGX.context.isFB === true && 'undefined' !== typeof FB) {
        resizeCanvas();
      }

      // If this is NOT the initial page/tab routing action (consider on initial page load, we don't want the
      // customers parent window jumping down to the top of our iframe, or indeed jumping anywhere at all, it should stay where it is).
      if (!initialPage) {
        // The Y coordinate we want to jump the parent page to, default to 0 (top).
        var yPos = 0;

        // If campaign is framed.
        if (NGX.App.state.framed) {
          // Just in case something unanticipated goes wrong with the x-frame communication, better to scroll incorrectly than break the campaign entirely.
          try {
            // If pageInfo object is populated, use it.
            if (NGX.App.state.pageInfo && NGX.App.state.pageInfo.offsetTop) {
              yPos += NGX.App.state.pageInfo.offsetTop;
              NGX.App.api.scrollTo(0, yPos);

              // Sometimes, the pageInfo object does not appear to be fully populated at this point (some type of race condition?),
              // so in this situation, explicitly request the information now, supplying an inline callback.
            } else {
              NGX.App.api.messageParent({ action: 'getpageinfo' }, function(info) {
                yPos += info.offsetTop;
                NGX.App.api.scrollTo(0, yPos);
              });
            }
          } catch (exception) {}

          // If campaign is not framed.
        } else {
          NGX.App.api.scrollTo(0, yPos);
        }
      }

      // TODO: add code to handle scroll-to the now visible state panels top?
    },
    /** AppFlow specific items */

    /**
     * Trigger componentView method for components on this tab
     * @param status
     * @param el
     */
    componentsStatus = function(status, el) {
      $('[data-componenttype]', el).each(function(i, item) {
        if (item.id) {
          // update 'viewed' attribute in component model
          NGX.App.Components.get(item.id).set('viewed', true);
        }
      });
    },
    /**
     * Update any nav items for the current state
     */
    updateNav = function() {
      var attr = 'data-ngx-action',
        update = function() {
          var data = this.getAttribute(attr);
          var $this = $(this);
          var route;
          // Add 'current' class to current route, remove it from others
          if (data && data === 'route:' + _current.route) {
            $this.addClass('current');
            $this.attr('aria-page', 'current');
          } else {
            $this.removeClass('current');
            $this.removeAttr('aria-page');
          }
          // For confirmation state (SPA) add or remove 'xHidden' class
          if (flowJSON.routes.hasOwnProperty('activate')) {
            route = flowJSON.routes.activate.states.indexOf('state:' + _current.route) > -1 ? 'Activate' : 'Start';
            var $parent = $this.parents('li');
            $parent[$parent.hasClass('in' + route) ? 'removeClass' : 'addClass']('xHidden');
          }
        };
      // TODO: remove these bits of jQuery? it's kinda slow
      $('nav')
        .find('a[' + attr + ']')
        .each(update);
    },
    /**
     * Show state as overlay if marked as such in the flow
     * @param {string} state
     * @returns {Dom element}
     */
    showTabAsOverlay = function(state) {
      NGX.App.api.messageParent(
        {
          action: 'getPageInfo',
        },
        function(info) {
          NGX.App.state.pageInfo = info;
        }
      );
      var el = d.getElementById('xSection' + capitalize(state));
      if (el) {
        var $el = $(el),
          $content = $('.xSectionInner', $el);

        NGX.overlay.create({
          id:      'xO-state',
          content: $content.html(),
          type:    'state',
        });

        // update the xState[stateName] body class
        updateClassByPrefix(body, 'xState', state);
        // set 'show' model value of components on incoming panel
        componentsStatus('show', el);
        // return the active section
        return el;
      }
    },
    /**
     * Show state as full page (tab)
     * @param {string} state
     * @returns {Dom element | boolean}
     */
    showTab = function(state) {
      var rxA = /\bxSectionContent\b/,
        //rxB = /\bxSectionActive\b/,
        el = d.getElementById('xSection' + capitalize(state)),
        n,
        nClass,
        nClassArr = ['xHidden', 'xSectionActive'];
      if (el && el.parentNode) {
        n = el.parentNode.firstChild;
        // loop through siblings
        for (; n; n = n.nextSibling) {
          // its an html node with a class
          if (n.nodeType === 1 && n.hasAttribute('class')) {
            // not using n.className to support IE8 quirks mode
            nClass = n.getAttribute('class');
            // is a section
            if (rxA.test(nClass)) {
              // is currently active
              // TODO: replace the jQuery selector here with DOM JS
              $(n)
                .addClass(nClassArr[n !== el ? 0 : 1])
                .removeClass(nClassArr[n !== el ? 1 : 0] + ' xOverlay');
            }
          }
        }
        // update the navigate panel hi-light
        updateNav();
        // update the state-stateName body class
        updateClassByPrefix(body, 'xState', state);
        // set 'show' model value of components on incoming panel
        componentsStatus('show', el);
        // resize the iframe to fit the content
        screenPosition();
        // If we are on the confirmation page, mark all headings as focusable for the screenreader
        if (state === 'confirmation') {
          const headingEl = $('h1,h2,h3,h4,h5,h6', el)[0];
          headingEl.setAttribute('tabindex', 0);
          headingEl.focus();
        }
        // return the active panel
        return el;
      }
      // no panel matching that state
      return false;
    },
    /**
     * Show promotion as full page (tab)
     * @param {string} promo - markup
     * @returns {Dom element | boolean}
     */
    showPromoAsTab = function(promo) {
      if (NGX.App.state.shownPromo) {
        log.debug('Promotion already shown, exiting', 1);
        return false;
      }

      var rxA = /\bxSectionContent\b/,
        n,
        nClass,
        nClassArr = ['xHidden', 'xSectionActive'];

      var el = document.createElement('div'),
        promoEl;
      var pageMain = document.querySelector('.xPageMain');
      var _callback, existEl;
      el.innerHTML = trim(promo);

      // Look through promos and make sure they don't already exist
      for (let e = 0, en = el.childNodes.length; e < en; e++) {
        if (el && el.childNodes[e] && el.childNodes[e].nodeType === 1) {
          const _n = el.childNodes[e];
          if (_n.tagName === 'SECTION') {
            promoEl = _n;

            if ((existEl = document.getElementById(promoEl.id))) {
              log.debug('Promo already exists in the DOM, remove it', 2);
              existEl.parentNode.removeChild(existEl);
            }
          }
        }
      }

      if (!promoEl) {
        log.debug('There was no content in the selected page', 1);
        return false;
      }

      // Loop over each element of the promo and handle based on type
      for (let e = 0, en = el.childNodes.length; e < en; e++) {
        if (el && el.childNodes[e] && el.childNodes[e].nodeType === 1) {
          const _n = el.childNodes[e];
          if (_n.tagName === 'SCRIPT') {
            _callback = function() {
              addInlineScript(_n.text, '__ngxPromoScript', null, true);
              DisplayItem.applyClickHandlers();
            };
          } else if (_n.tagName === 'STYLE') {
            _n.id = '__ngxPromoStyles';
            pageMain.appendChild(_n);
          } else if (_n.tagName === 'SECTION') {
            _n.insertBefore(createCloseButtons('promo', 'promo:close:' + _n.id), _n.querySelector('.xSectionInner'));
            _n.appendChild(
              createCloseButtons('promo', 'promo:close:' + _n.id, true),
              _n.querySelector('.xSectionInner')
            );

            pageMain.appendChild(_n);

            // Set state variable with shown promotion
            NGX.App.state.shownPromo = _n.id;
          }
        }
      }

      NGX.App.Events.once('promo:ready', function() {
        NGX.App.api.elementIntoView($('.xPage'));
        NGX.App.api.resizeParent();
      });

      // Find and hide each module section but show overlay
      if (pageMain && pageMain.childNodes) {
        n = pageMain.firstChild;
        // loop through siblings
        for (; n; n = n.nextSibling) {
          // its an html node with a class
          if (n.nodeType === 1 && n.hasAttribute('class')) {
            // not using n.className to support IE8 quirks mode
            nClass = n.getAttribute('class');
            // is a section
            if (rxA.test(nClass)) {
              // is currently active
              // TODO: replace the jQuery selector here with DOM JS
              $(n)
                .addClass(nClassArr[n !== promoEl ? 0 : 1])
                .removeClass(nClassArr[n !== promoEl ? 1 : 0] + ' xOverlay');
            }
          }
        }
        document.querySelector('.xSectionActive').classList.add('xInlinePromo');

        // update the navigate panel hi-light
        updateNav();
        // update the state-stateName body class
        updateClassByPrefix(body, 'xState', 'Promo');
        // set 'show' model value of components on incoming panel
        componentsStatus('show', el);
        // resize the iframe to fit the content
        screenPosition();
        // evaluate incoming JS
        _callback.call(this);
        // trigger the ready event
        NGX.App.Events.trigger('promo:ready');
        // return the active panel
        return el;
      }
      // no panel matching that state
      return false;
    };

  /** States */

  var State = function() {
    /**
     * Invoke the new state from an action
     * @param {Object} newState
     * @returns {*}
     */
    var stateAction = function(newState) {
        var action = newState.action;
        if (action && action.indexOf('state') !== 0) {
          // action caller is state:stateRoute, full action name is action:state:stateRoute
          return _action.trigger(action, _current);
          //TODO: no action event is triggered for state actions, since they're the product of a state. this would .trigger('action:state:stateRoute');
          //return _action.trigger(action, _current, 'state:'+_current.route);
        }
        log.debug('Unable to execute State action "' + action + '"', 1);
        return true; // we have entered a state at this point, return true even if there is no action
      },
      /**
       * Invoke the new state by name
       * @param {string} name
       * @event 'state'
       * @event 'navigate:page'
       * @event 'state:<state name>'
       * @event 'navigate:page:<state name>'
       * @returns {*}
       */
      stateInvoke = function(name) {
        var newState = stateCache[name],
          init = stateInit[name],
          doState = function() {
            _prev = _current;
            _current = newState;
            // trigger pseudo-asyncronously to prevent .on('state') event blocking stateAction()
            asyncFn(function() {
              NGX.Analytics.trigger('navigate:page', { pageName: newState.name });
              if (AllAnalyticsEvents.indexOf(newState.name) !== -1) {
                NGX.Analytics.trigger('navigate:page:' + newState.name);
              }

              NGX.App.Events.trigger('state', newState.name, newState);
              NGX.App.Events.trigger('navigate:page:', newState.name, newState);
              NGX.App.Events.trigger('state:' + newState.name, newState);
              NGX.App.Events.trigger('navigate:page:' + newState.name, newState);
              NGX.App.api.messageParent({
                action:  'event',
                event:   'state',
                payload: {
                  name: newState.name,
                },
              });
            });
            log.debug('entered State "' + newState.name + '", action is "' + newState.action + '"', 2);
            return stateAction(newState);
          };

        // TODO: do we need more state object validation here?
        if (newState) {
          // an init function exists
          if (init) {
            // if async pass the action function
            if (init.async === true) {
              return init.fn(doState);
            }
            // otherwise just do it now
            init.fn();
          }
          return doState();
        }
        log.debug('Unable to change state "' + name + '"', 1);
        return false; // we have not entered a state
      },
      /**
       * Prevent state from being re-initialised
       * @param {string} name
       * @returns {boolean}
       */
      removeStateInit = function(name) {
        if (stateInit[name]) {
          delete stateInit[name];
          log.debug('State event "beforeState:' + name + '" removed', 2);
          return true;
        }
        return false;
      },
      /**
       * Create a callback for initialisation of a state
       * @param {string} name
       * @param {Function} fn
       * @param {boolean} isAsync
       * @returns {boolean}
       */
      addStateInit = function(name, fn, isAsync) {
        var out = false;
        if (name && typeof fn === 'function') {
          out = stateInit[name] = {
            async: isAsync || false,
            fn:    fn,
          };
          log.debug('State event "' + name + '" created', 2);
        }
        return out;
      };

    // Public API
    return {
      get: function(obj) {
        return _.findWhere(json.states, obj);
      },
      invoke:   stateInvoke,
      register: addStateInit,
      remove:   removeStateInit,
    };
  };

  /** Actions */

  var Action = function() {
    // action behaviours
    var method = {
        /**
         * Execute an action
         * @param {Object} obj
         * @returns {*|boolean}
         */
        exec: function(obj) {
          var fn = NGX.App.api[obj.action];
          if (typeof fn === 'function') {
            // TODO: new sharing method uses an object passed, rather than an array .apply() - consider refactoring to bring into line
            return fn.apply(root, obj.params);
          }
          log.debug('API method "' + obj.action + '" not found', 1);
          return false;
        },
        /**
         * Display actions
         * @param {Object} obj
         * @param {Object} params
         * @returns {*}
         */
        display: function(obj, params) {
          switch (obj.action) {
            // Display a states content as an overlay
            case 'dialog':
              return NGX.overlay.create({
                content: d.getElementById('xSection' + capitalize(obj.target)).innerHTML,
                onclose: function() {
                  if (_prev) {
                    NGX.appFlow.prev();
                  }
                },
              });
            // Display a campaign or state as an overlay or tab
            case 'campaign':
            case 'page':
              // if the state style definition is overlay
              if (params && params.style === 'overlay') {
                return showTabAsOverlay(obj.target);
              }

              var showTabResponse = showTab(obj.target);

              // initial page/tab has now been displayed, set flag to false.
              initialPage = false;

              // Check if we were previously showing a promo as a tab, and re-focus it if required.
              if (NGX.App.state.shownPromo) {
                // Hide the current section.
                var el = d.getElementById('xSection' + capitalize(obj.target));
                $(el)
                  .addClass('xHidden')
                  .removeClass('xSectionActive');

                // Show the promo section.
                var promoEl = d.getElementById(NGX.App.state.shownPromo);
                $(promoEl)
                  .removeClass('xHidden')
                  .addClass('xSectionActive');
              }

              return showTabResponse;
            // Display a promotion as an overlay
            case 'promo':
              log.debug('Display Promo: ' + obj.target, 5);
              const _ctx = {
                id:  obj.target,
                cID: NGX.info.id,
              };

              if ((NGX.context.isFB || NGX.context.isFBTab) && NGX.context.fb) {
                if (NGX.context.fb.app && NGX.context.fb.app.id) _ctx.fb_app_id = NGX.context.fb.app.id;
                if (NGX.context.fb.page && NGX.context.fb.page.id) _ctx.fb_page_id = NGX.context.fb.page.id;
              }

              if (NGX.context.isStage) {
                (_ctx.stageMode = true), (_ctx.ptk = NGX.context.stageToken);
              }

              // WYN-1839
              // Controller is expecting to recieve display container id under param 'sID'.
              if (NGX.context.displayContainer) {
                _ctx.sID = NGX.context.displayContainer.id;
              }
              _ctx.apikey = NGX.context.apikey;

              const _url = NGX.Util.updateQueryString(_ctx, true, '/display/promotion/ajaxOverlay');

              if (params && params.details == 'inline') {
                $.get(_url).success(function(content, status, xhr) {
                  NGX.App.Events.once('promo:ready', function() {
                    NGX.App.api.getComponents();
                  });
                  showPromoAsTab(content);
                });
              } else {
                NGX.overlay.create({
                  url:      _url,
                  type:     'promo',
                  callback: function() {
                    $('.xComponent', '.xOverlayInner').on('click', '.xContainer .xTriggerOverlay', initOverlayHandler);
                    NGX.App.api.getComponents();
                    NGX.App.Events.trigger('promo:ready');
                    NGX.App.Events.once(
                      'collection:rendered',
                      function() {
                        this.center();
                      }.bind(this)
                    );
                  },
                  onclose: function() {
                    log.debug('Closing the overlay', 6);
                    removeFormListeners();
                  },
                });
              }

              // Track the promo view
              NGX.Insights.track('view:promo', {
                p: obj.target,
              });
              break;

            // Display a content item as an overlay
            case 'item':
              log.debug('Display Item: ' + obj.target, 5);
              popOverlayForDisplayItem(obj.target);
              break;
          }
          return false;
        },
        /**
         * Dispatches a share action
         * @param {Object} obj
         * @param {Object} params
         * @event 'action:share'
         * @returns {*}
         */
        share: function(obj, params) {
          var shareContext = NGX.App.api.share[obj.action],
            fn;
          if (shareContext) {
            fn = shareContext[obj.target];
            params.context = obj.action;
            if (typeof fn === 'function') {
              log.debug(obj.target + ' ' + obj.action + ' share prompt', 2);
              // Analytics API: only allow valid analytics events, but not social sharing (they
              // trigger further downstream to collect required params).
              if (
                AllAnalyticsEvents.indexOf('action:' + params.action) !== -1 &&
                !params.action.startsWith('share:campaign:')
              ) {
                NGX.Analytics.trigger('action:' + params.action);
              }
              NGX.App.Events.trigger('action:share', params);
              return fn.call(root, params);
            }
          }
          return false;
        },

        /**
         * Trigger a route to a state
         * @param obj
         * @param params
         * @returns {string}
         */
        route: function(obj, params) {
          /**WYN-6475. Close the promo before navigation (if it is opened) */
          if (NGX.App.state.shownPromo) {
            triggerByRef('promo:close:'+NGX.App.state.shownPromo, {action: 'promo:close:'+NGX.App.state.shownPromo});
          }
          // if a target id is supplied, add it to the route
          var route = obj.action + (obj.target ? '/' + obj.target : '');
          log.debug('routing to "' + route + '"', 2);
          // actual routing event
          Backbone.history.navigate(route, {
            trigger: true,
            replace: false,
          });
          // update URL with query string
          Backbone.history.navigate(route + NGX.App.api.getQuerystring(), {
            trigger: false,
            replace: true,
          });
          return route;
        },

        /**
         * Trigger network actions (Twitter is the only network that has actions, currently)
         * @param {Object} obj
         * @param {Object} params
         */
        network: function(obj, params) {
          switch (obj.action) {
            case 'twitter':
              var url = 'twitter.com/intent/' + (obj.target == 'reply' ? 'tweet' : obj.target),
                twParams = {};
              twParams[obj.target == 'reply' ? 'in_reply_to' : 'tweet_id'] = obj.params[1];

              openWindow(url, twParams);
              break;
          }
          // Track the network action
          NGX.Insights.track(obj.target + ':contentitem', {
            action: obj.target,
            ci:     obj.params[2],
            target: obj.action,
          });
        },

        /**
         * Upate the application's state
         * @param {Object} obj
         * @returns {*}
         */
        state: function(obj) {
          // prevent double state entry
          if (_current && _current.route === obj.action) {
            log.debug('Cannot re-trigger current active state "' + obj.action + '"', 1);
            // ALLOW the current state to be re-triggered as a screen reset
            //return false;
          }
          return _state.invoke(obj.action);
        },

        /**
         * Redirect to a content item's external link
         * @param {Object} obj
         * @param {Object} params
         * @returns {boolean}
         */
        redirect: function(obj, params) {
          // prevent double state entry
          if (_current && _current.route === obj.action) {
            log.debug('Cannot re-trigger current active state "' + obj.action + '"', 1);
            return false;
          }
          // Track the redirect
          NGX.Insights.track('redirect:contentitem', { i: obj.target, link: params.link });

          // Open link in a new window if the component config requests it
          if (params.ctatarget == '_blank') {
            window.open(params.link);
          } else {
            document.location = params.link;
          }
        },

        /**
         * Allow voting directly from an action
         * @param {Object} obj
         */
        vote: function(obj) {
          NGX.contests.doVote(null, obj.target);
        },

        /**
         * Promotion related actions
         */
        promo: function(obj) {
          switch (obj.action) {
            case 'close':
              removeFormListeners();
              const _promo = document.getElementById(obj.target),
                _promoScript = document.getElementById('__ngxPromoScript'),
                _promoStyles = document.getElementById('__ngxPromoStyles');
              if (_promo) _promo.parentNode.removeChild(_promo);
              if (_promoScript) _promoScript.parentNode.removeChild(_promoScript);
              if (_promoStyles) _promoStyles.parentNode.removeChild(_promoStyles);
              $('[data-statename=' + NGX.App.state.name + ']').removeClass('xHidden');
              updateClassByPrefix(body, 'xState', NGX.App.state.name);
              // Update state variable
              NGX.App.state.shownPromo = null;
              NGX.App.api.resizeParent();

              try {
                $('.xTmpl-Masonry').each(function() {
                  var $msnry = window['p' + this.id];
                  if ($msnry.is(':visible')) {
                    $msnry.packery('layout');
                    NGX.App.api.resizeParent();
                  }
                });
              } catch (e) {
                alert(e);
              }

              break;
          }
        },
      },
      /**
       * Return an action object by name, if valid
       * @param name
       * @returns {Object|boolean}
       */
      getActionObjByName = function(name) {
        var obj = actionJSON[name];
        return obj && obj.action ? obj : false;
      },
      /**
       * Get an action ref from a named action if valid
       * @param name
       * @returns {string|boolean}
       */
      getActionRefByName = function(name) {
        var obj;
        if (name && !isRef(name)) {
          obj = actionJSON[name];
          if (obj && obj.action) {
            return obj.action; // return the action ref string
          }
          return false; // nothing matches
        }
        return name; // treat as action ref
      },
      /**
       * Trigger an action based on its action ref.
       * @param {string} refStr
       * @param {Object} params
       * @param {String} caller (optional param that contains the named action that triggered the action by ref)
       * @returns {*}
       */

      triggerByRef = function(refStr, params, caller) {
        log.debug('triggerByRef = ' + refStr, 3);
        var obj = getRefObj(refStr),
          fn = method[obj.method],
          init = actionInit[caller],
          out = function() {
            var fnOut = fn(obj, params);
            // if an onAction tracking event is passed in trigger it now // TODO: this used to depend on 'caller' being present, but surely that sucks?
            // ensure this happens after the delayed state events (and its own callback) are triggered so events always execute in order, see stateInvoke()
            asyncFn(
              function() {
                // TODO: [RJ] caller is a product of the custom 'before' events, refStr is a regular ref string.
                var str = caller || refStr;
                NGX.App.Events.trigger('action', str);
                NGX.App.Events.trigger('action:' + str, fnOut);
                // TODO: [RJ] there's a reason we pass fnOut but i can't remember what it was! put a comment here when i have time to chase it through.
              }.bind(this)
            );
            return fnOut;
          };
        if (typeof fn === 'function') {
          // an init function exists
          if (init && caller) {
            // if async pass the action function
            if (init.async === true) {
              return init.fn(out);
            }
            // otherwise just do it now
            init.fn();
          }
          return out();
        }
        log.debug('Method "' + obj.method + '" is not a function', 1);
        return false;
      },
      /**
       * Trigger an action by name
       * @param {string} name
       * @returns {*}
       */
      triggerByName = function(name) {
        log.debug('triggerByName = ' + name, 3);
        var obj = getActionObjByName(name);
        if (obj) {
          return triggerByRef(obj.action, obj, name);
        }
        log.debug('Action "' + name + '" not found', 1);
        return false;
      },
      /**
       * triggerByValue is the action ref entry point.
       *
       * Object 'params' will either be passed in from the State handler and will contain the State definition,
       * otherwise it will be constructed from the any html attributes prefixed with 'data-ngx-' from the clicked
       * element. (handled in ui.core.js)
       *
       * String 'caller' is used to track which State triggered an action, and will generally only be provided by
       * the internal calls to this method. Use it at your own peril :P
       */

      triggerByValue = function(action, params, caller) {
        log.debug('triggerByValue = ' + action, 3);
        if (action) {
          if (isRef(action)) {
            return triggerByRef(action, params, caller);
          }
          return triggerByName(action);
        }
        log.debug('No action specified', 1);
        return false;
      },
      /**
       * Create an init function for an action
       * @param {string} name
       * @param {Function} fn
       * @param {boolean} isAsync
       * @returns {boolean}
       */
      addActionInit = function(name, fn, isAsync) {
        var out = false;
        if (typeof fn === 'function') {
          if (name) {
            out = actionInit[name] = {
              async: isAsync || false,
              fn:    fn,
            };
            log.debug('Action event "beforeAction:' + name + '" created', 2);
          }
        }
        return out;
      },
      /**
       * Removes an init function, prevents it from being called > once
       * @param {string} name
       * @returns {boolean}
       */
      removeActionInit = function(name) {
        if (actionInit[name]) {
          delete actionInit[name];
          log.debug('Action event "beforeAction:' + name + '" removed', 2);
          return true;
        }
        return false;
      },
      /**
       * Adds a new action
       * @param {string} name
       * @param {Object} obj
       * @param {boolean} overwrite (optionally overwrite the existing named action
       * @returns {boolean}
       */
      registerAction = function(name, obj, overwrite) {
        var out = false;
        if (!actionJSON[name] || overwrite === true) {
          if (typeof obj === 'object' && obj.action) {
            out = actionJSON[name] = obj;
            log.debug('Action "' + name + '" registered', 2);
          }
        }
        return out;
      };

    // Public API
    return {
      byName:    triggerByName,
      byRef:     triggerByRef,
      trigger:   triggerByValue,
      getAction: getActionObjByName,
      getRef:    getActionRefByName,
      register:  addActionInit,
      remove:    removeActionInit,
      regAction: registerAction,
    };
  };

  return {
    /**
     * Attach a new appflow event
     * @param {string} ref
     * @param {Function} fn
     * @param {boolean} optional
     * @returns {*}
     */
    registerEvent: function(ref, fn, optional) {
      var arr, name, type;
      if (isRef(ref)) {
        arr = ref.split(':');
        type = arr[0];
        name = arr[1];
        // check there is a name and a valid function
        if (!name || typeof fn !== 'function') {
          return false;
        }
        // create the right type of onX event, or beforeX pseudo-event
        switch (type) {
          case 'beforeAction':
            return _action.register(name, fn, optional);
          case 'beforeState':
            return _state.register(name, fn, optional);
          case 'onAction':
          case 'onState':
            return NGX.App.Events.on(type.substr(2).toLowerCase() + ':' + name, fn);
        }
      }
      log.debug('unrecognised action ref syntax "' + ref + '"', 1);
      return false;
    },

    /**
     * Detach an event from appflow
     * @param {string} ref
     * @returns {*}
     */
    removeEvent: function(ref) {
      var arr, name, type;
      if (isRef(ref)) {
        arr = ref.split(':');
        type = arr[0];
        name = arr[1];
        switch (type) {
          case 'beforeAction':
            return _action.register(name);
          case 'beforeState':
            return _state.register(name);
          case 'onAction':
          case 'onState':
            return NGX.App.Events.off(type.substr(2).toLowerCase() + ':' + name);
        }
      }
      log.debug('unrecognised action ref syntax "' + ref + '"', 1);
      return false;
    },

    /**
     * revert the app to the previous state (used by 'close' buttons on state overlays)
     */
    prev: function() {
      // failover is the first state in the object, as no defaultState may be present // TODO: first try to failover to default state?
      var stateName = json.states[0].name;
      // HOTFIX: if the first item is the current state, grab the next.
      if (stateName === _current.name) {
        stateName = json.states[1].name;
      }
      // if a previous state is available and isn't the current route...
      if (typeof _prev === 'object' && _prev.route) {
        stateName = _prev.route;
      }
      // navigate to the state provided
      NGX.App.api.state(stateName);
    },

    /**
     * Some events should only trigger if this is the default route (a.k.a start route). Expose this to
     * help these events.
     */
    isStartRoute: isStartRoute,

    /**
     * Initialise the appflow engine
     * @returns {{state, action, register: (registerEvent|Function), remove: *}}
     */
    init: function() {
      return {
        // prevent multiple state and action objects from being instantiated.
        state: function() {
          if (!_state) {
            _state = new State();
          }
          return _state;
        }.call(this),
        action: function() {
          if (!_action) {
            _action = new Action();
          }
          return _action;
        }.call(this),
        register: this.registerEvent,
        remove:   this.removeEvent,
      };
    },
  };
})(window, document, $, flowJSON);
